import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchDispensingMasters, createDispensingMaster, updateDispensingMaster, deleteDispensingMaster, seedDispensingMasters } from "../../../slices/company/dispensingMasterSlice";
import { fetchDispenseMaterials } from "../../../slices/company/dispenseMaterialSlice";
import DispensingMasterForm from "./dispensing_master_form";
import DataTable from 'react-data-table-component';
import { customTableStyles } from "../../../utils/tableStylings";
import { Delete, Pen, Database, Info, Layers, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORIES = [
  { key: 'ALL', label: 'All' },
  { key: 'TRITURATION', label: '💊 Trituration (Apr. 24)' },
  { key: 'MOTHER_TINCTURE', label: '🧪 Mother Tincture (Groups A-J)' },
  { key: 'BIOCHEMIC', label: '🌿 Biochemic (3X,6X,12X,30X)' },
  { key: 'GLOBULES_REF', label: '🍬 Globules & Bottle Ref' },
  { key: 'DILUTION_LIQUID', label: '💧 Dilution - Liquid' },
  { key: 'MEDICATED_GLOBULES', label: '🍬 Medicated Globules' },
  { key: 'SAC_LAC_DOSE', label: '✉️ Sac Lac Dose' },
  { key: 'DW_DOSE', label: '🚰 D/W Dose' },
];

const OPERATIONAL_NOTES = [
  { title: 'Trituration Conversion (Apr. 24)', text: 'Category A to F rates apply for 10gm, 15gm, 20gm, 30gm, 60gm, 100gm and Puria doses. Category is mapped from Tri sheet potencies.' },
  { title: 'Mother Tinctures (CNT)', text: 'Groups A to J prices configured for 5ml, 10ml, 15ml, 20ml, 30ml, 60ml, 100ml. No alcohol added for Mother Tinctures.' },
  { title: 'Spirit Formulation', text: 'Remaining mls is filled by Alcohol. No alcohol will be used for mother tincture.' },
  { title: 'Package Standards', text: '25kg package only.' },
  { title: 'Puria Production', text: '32,500 count approx. total puria from 25kg (Sagar).' },
  { title: 'Sales Workflow', text: 'Select → Trituration / Mother Tincture / Dilution → Choose size. Amount is calculated automatically from Category Masters.' },
];

const TRITURATION_MATRIX_SIZES = ['10gm', '15gm', '20gm', '30gm', '60gm', '100gm', 'Puria'];
const TRITURATION_MATRIX_CATS = ['Category A', 'Category B', 'Category C', 'Category D', 'Category E', 'Category F'];

const resolveRuleMaterials = (rule, materials) => {
  const resolved = [];
  if (!materials || materials.length === 0) return resolved;

  // 1. Bottle
  if (rule.bottleSize) {
    const sizeStr = rule.bottleSize.toString().toUpperCase().trim();
    const sizeVal = sizeStr.endsWith('ML') ? sizeStr : `${sizeStr}ML`;
    const bottleType = rule.bottleType || 'Regular';

    let mat = materials.find(m => m.material_type === 'bottle' && m.bottle_type === bottleType && m.size_val === sizeVal);
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('bottle') && name.includes(sizeStr.toLowerCase()) && 
          (bottleType === 'L' ? name.includes('l bottle') : !name.includes('l bottle'));
      });
    }
    if (mat) resolved.push({ material: mat, qty: 1 });
  }

  // 2. White Cap
  if (parseFloat(rule.whiteCapQty) > 0) {
    const qty = parseFloat(rule.whiteCapQty);
    const num = rule.whiteCapNo ? rule.whiteCapNo.toString().trim() : '';
    const formattedNum = num ? (num.startsWith('No.') ? num : `No. ${num}`) : '';

    let mat = materials.find(m => m.material_type === 'white_cap' && (formattedNum ? m.number_val === formattedNum : true));
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('white') && name.includes('cap') && (num ? name.includes(num.toLowerCase()) : true);
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 3. Seal Cap
  if (parseFloat(rule.sealCapQty) > 0) {
    const qty = parseFloat(rule.sealCapQty);
    const num = rule.sealCapNo ? rule.sealCapNo.toString().trim() : '';
    const formattedNum = num ? (num.startsWith('No.') ? num : `No. ${num}`) : '';

    let mat = materials.find(m => m.material_type === 'seal_cap' && (formattedNum ? m.number_val === formattedNum : true));
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('seal') && name.includes('cap') && (num ? name.includes(num.toLowerCase()) : true);
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 4. Dropper
  if (parseFloat(rule.dropperQty) > 0) {
    const qty = parseFloat(rule.dropperQty);
    const size = rule.dropperNo ? rule.dropperNo.toString().toUpperCase().trim() : '';
    const sizeVal = size ? (size.endsWith('ML') ? size : `${size}ML`) : '';

    let mat = materials.find(m => m.material_type === 'dropper' && (sizeVal ? m.size_val === sizeVal : true));
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('dropper') && (size ? name.includes(size.toLowerCase()) : true);
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 5. Push
  if (parseFloat(rule.pushQty) > 0) {
    const qty = parseFloat(rule.pushQty);
    const num = rule.pushNo ? rule.pushNo.toString().trim() : '';
    const formattedNum = num ? (num.startsWith('No.') ? num : `No. ${num}`) : '';

    let mat = materials.find(m => m.material_type === 'push' && (formattedNum ? m.number_val === formattedNum : true));
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('push') && (num ? name.includes(num.toLowerCase()) : true);
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 6. Label
  if (rule.labelSize) {
    const size = rule.labelSize.toString().toUpperCase().trim();
    const sizeVal = size.endsWith('ML') ? size : `${size}ML`;

    let mat = materials.find(m => m.material_type === 'label' && m.size_val === sizeVal);
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('label') && name.includes(size.toLowerCase());
      });
    }
    if (mat) resolved.push({ material: mat, qty: 1 });
  }

  // 7. Alcohol
  if (parseFloat(rule.alcoholQty) > 0) {
    const qty = parseFloat(rule.alcoholQty);
    let mat = materials.find(m => m.material_type === 'alcohol');
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('alcohol') || name.includes('spirit');
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 8. Globules
  if (parseFloat(rule.globulesWeight) > 0) {
    const qty = parseFloat(rule.globulesWeight);
    let mat = materials.find(m => m.material_type === 'globules');
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('globule') || (name.includes('sugar') && !name.includes('saclac') && !name.includes('lactose'));
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 9. Puria
  if (parseFloat(rule.puriaQty) > 0) {
    const qty = parseFloat(rule.puriaQty);
    let mat = materials.find(m => m.material_type === 'puria');
    if (!mat) {
      mat = materials.find(m => {
        const name = m.name.toLowerCase();
        return name.includes('puria') || name.includes('packet') || name.includes('paper');
      });
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  // 10. Water
  if (parseFloat(rule.waterQty) > 0) {
    const qty = parseFloat(rule.waterQty);
    let mat = materials.find(m => m.material_type === 'water');
    if (!mat) {
      mat = materials.find(m => m.name.toLowerCase().includes('water'));
    }
    if (mat) resolved.push({ material: mat, qty });
  }

  return resolved;
};

const calculateRulePrice = (rule, materials) => {
  if (parseFloat(rule.sellingPrice) > 0) {
    return parseFloat(rule.sellingPrice);
  }
  const resolved = resolveRuleMaterials(rule, materials);
  return resolved.reduce((sum, item) => sum + item.qty * (parseFloat(item.material.price) || 0), 0);
};

const formatDeductions = (row) => {
  const parts = [];
  if (row.bottleSize) parts.push(`Bottle: ${row.bottleSize} (${row.bottleType || 'Regular'})`);
  if (parseFloat(row.whiteCapQty) > 0 && row.whiteCapNo) parts.push(`White Cap: ${row.whiteCapNo} x${parseFloat(row.whiteCapQty)}`);
  else if (parseFloat(row.whiteCapQty) > 0) parts.push(`White Cap: x${parseFloat(row.whiteCapQty)}`);
  if (parseFloat(row.sealCapQty) > 0 && row.sealCapNo) parts.push(`Seal Cap: ${row.sealCapNo} x${parseFloat(row.sealCapQty)}`);
  else if (parseFloat(row.sealCapQty) > 0) parts.push(`Seal Cap: x${parseFloat(row.sealCapQty)}`);
  if (parseFloat(row.dropperQty) > 0 && row.dropperNo) parts.push(`Dropper: ${row.dropperNo} x${parseFloat(row.dropperQty)}`);
  else if (parseFloat(row.dropperQty) > 0) parts.push(`Dropper: x${parseFloat(row.dropperQty)}`);
  if (parseFloat(row.pushQty) > 0 && row.pushNo) parts.push(`Push: ${row.pushNo} x${parseFloat(row.pushQty)}`);
  else if (parseFloat(row.pushQty) > 0) parts.push(`Push: x${parseFloat(row.pushQty)}`);
  if (row.labelSize) parts.push(`Label: ${row.labelSize}`);
  if (parseFloat(row.alcoholQty) > 0) parts.push(`Alcohol: ${parseFloat(row.alcoholQty)}ml`);
  if (parseFloat(row.globulesWeight) > 0 && row.globulesSize) parts.push(`Globules: ${row.globulesSize} (${parseFloat(row.globulesWeight)}g)`);
  else if (parseFloat(row.globulesWeight) > 0) parts.push(`Globules: ${parseFloat(row.globulesWeight)}g`);
  if (parseFloat(row.puriaQty) > 0) parts.push(`Puria: ${parseFloat(row.puriaQty)}pcs`);
  if (parseFloat(row.waterQty) > 0) parts.push(`Water: ${parseFloat(row.waterQty)}ml`);
  return parts.length > 0 ? parts.join(", ") : "-";
};

const DispensingMaster = () => {
  const dispatch = useDispatch();
  const { masters, loading, error } = useSelector((state) => state.dispensingMaster);
  const { materials } = useSelector((state) => state.dispenseMaterial);
  const [showModal, setShowModal] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [modalHeading, setModalHeading] = useState("");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [showNotes, setShowNotes] = useState(false);
  const [tritCatFilter, setTritCatFilter] = useState("ALL");

  const company = JSON.parse(localStorage.getItem("company"));
  const companyId = company?.company?.id;

  useEffect(() => {
    dispatch(fetchDispensingMasters(activeCategory));
    if (companyId) {
      dispatch(fetchDispenseMaterials(companyId));
    }
  }, [dispatch, activeCategory, companyId]);

  const handleAdd = () => {
    setSelectedMaster(null);
    setModalHeading("Create New Dispensing Rule");
    setShowModal(true);
  };

  const handleEdit = (master) => {
    setSelectedMaster(master);
    setModalHeading("Edit Dispensing Rule");
    setShowModal(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "<strong>Are you sure?</strong>",
      html: `<p>This action will <b>permanently delete</b> this Dispensing Rule.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteDispensingMaster(id));
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The Dispensing Rule has been deleted.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    });
  };

  const handleSeed = () => {
    Swal.fire({
      title: "<strong>Seed from Excel Reference?</strong>",
      html: `<p>This will <b>re-seed all reference rules</b> from the Excel sheets (Trituration Apr.24, Mother Tinctures, Biochemic, Dilution).</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Seed Data",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(seedDispensingMasters()).then((action) => {
          if (action.meta.requestStatus === 'fulfilled') {
            dispatch(fetchDispensingMasters(activeCategory));
            Swal.fire({
              icon: "success",
              title: "Seeded!",
              text: action.payload?.message || "Dispensing Master seeded successfully.",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
          } else {
            Swal.fire({ icon: "error", title: "Error", text: "Failed to seed data." });
          }
        });
      }
    });
  };

  const handleFormSubmit = (formData) => {
    if (selectedMaster && selectedMaster.id != null) {
      dispatch(updateDispensingMaster({ id: selectedMaster.id, masterData: formData })).then(() => {
        dispatch(fetchDispensingMasters(activeCategory));
      });
    } else {
      dispatch(createDispensingMaster(formData)).then(() => {
        dispatch(fetchDispensingMasters(activeCategory));
      });
    }
    setShowModal(false);
  };

  // Dynamic columns based on active category
  const getColumns = () => {
    const baseColumns = [
      {
        name: '#',
        selector: (row, index) => index + 1,
        width: '50px',
        sortable: false,
      },
    ];

    if (activeCategory === 'ALL') {
      baseColumns.push({
        name: 'Category',
        selector: row => CATEGORIES.find(c => c.key === row.category)?.label || row.category,
        sortable: true,
        width: '180px',
      });
    }

    switch (activeCategory) {
      case 'TRITURATION':
        return [
          ...baseColumns,
          { name: 'Trituration Category', selector: row => row.medicineType || '-', sortable: true, width: '180px' },
          { name: 'Size', selector: row => row.dispensingForm === 'PURIA' ? 'Puria' : `${row.salesSize} ${row.salesUnit}`, sortable: true, width: '120px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '140px' 
          },
          { name: 'Raw Material Used', selector: row => `${row.rawMaterialConsumed || 0} ${row.rawMaterialUnit || 'GM'}`, sortable: true, width: '160px' },
          { name: 'Packaging & Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'MOTHER_TINCTURE':
        return [
          ...baseColumns,
          { name: 'JNM Group', selector: row => row.medicineType || '-', sortable: true, width: '150px' },
          { name: 'Bottle Size', selector: row => `${row.salesSize} ${row.salesUnit}`, sortable: true, width: '130px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-primary fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '140px' 
          },
          { name: 'Packaging & Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'BIOCHEMIC':
        return [
          ...baseColumns,
          { name: 'Potency / Type', selector: row => row.medicineType || '3X,6X,12X,30X', sortable: true, width: '180px' },
          { name: 'Size', selector: row => `${row.salesSize} ${row.salesUnit}`, sortable: true, width: '130px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '140px' 
          },
          { name: 'Packaging & Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'GLOBULES_REF':
        return [
          ...baseColumns,
          { name: 'Weight/Volume', selector: row => `${row.salesSize} ${row.salesUnit}`, sortable: true },
          { name: 'Alternative Notation', selector: row => row.alternativeNotation || '-', sortable: true },
          { name: 'Bottle Sizes', selector: row => row.bottleSizes || '-', sortable: true },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'DILUTION_LIQUID':
        return [
          ...baseColumns,
          { name: 'Potency / Type', selector: row => row.medicineType || '-', sortable: true, width: '160px' },
          { name: 'Volume', selector: row => `${row.salesSize} ${row.salesUnit}`, sortable: true, width: '110px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '130px' 
          },
          { name: 'Medicine Required', selector: row => `${row.rawMaterialConsumed} ${row.rawMaterialUnit}`, sortable: true, width: '140px' },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'MEDICATED_GLOBULES':
        return [
          ...baseColumns,
          { name: 'Potency / Type', selector: row => row.medicineType || '-', sortable: true, width: '160px' },
          { name: 'Weight', selector: row => `${row.salesSize} ${row.salesUnit}`, sortable: true, width: '110px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '130px' 
          },
          { name: 'Globules Used', selector: row => `${row.rawMaterialConsumed} ${row.rawMaterialUnit}`, sortable: true, width: '130px' },
          { name: 'Drops', selector: row => row.drops || '-', sortable: true, width: '110px' },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'SAC_LAC_DOSE':
        return [
          ...baseColumns,
          { name: 'Type', selector: row => row.medicineType || '-', sortable: true, width: '150px' },
          { name: 'Dose Description', selector: row => row.doseCount || '-', sortable: true, width: '180px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '130px' 
          },
          { name: 'Drops', selector: row => row.drops || '-', sortable: true, width: '110px' },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      case 'DW_DOSE':
        return [
          ...baseColumns,
          { name: 'Type', selector: row => row.medicineType || '-', sortable: true, width: '150px' },
          { name: 'Dose Description', selector: row => row.doseCount || '-', sortable: true, width: '220px' },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => <strong className="text-success fs-14">₹{parseFloat(row.sellingPrice || 0).toFixed(2)}</strong>,
            sortable: true,
            width: '130px' 
          },
          { name: 'Drops', selector: row => row.drops || '-', sortable: true, width: '110px' },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];

      default: // ALL
        return [
          ...baseColumns,
          { name: 'Type', selector: row => row.medicineType, sortable: true },
          { name: 'Form', selector: row => row.dispensingForm || '-', sortable: true },
          { name: 'Size', selector: row => row.salesSize ? `${row.salesSize} ${row.salesUnit}` : (row.doseCount || '-'), sortable: true },
          { 
            name: 'Selling Price', 
            selector: row => parseFloat(row.sellingPrice) || 0,
            cell: row => parseFloat(row.sellingPrice) > 0 ? <strong className="text-success">₹{parseFloat(row.sellingPrice).toFixed(2)}</strong> : '-',
            sortable: true 
          },
          { name: 'Deductions', selector: row => formatDeductions(row), wrap: true, grow: 2 },
          actionColumn(),
        ];
    }
  };

  const actionColumn = () => ({
    name: 'Actions',
    cell: row => (
      <>
        <Link
          className="text-warning me-2"
          onClick={() => handleEdit(row)}
          title="Edit Price/Rule"
        >
          <Pen size={16} />
        </Link>
        <Link
          className="text-danger"
          onClick={() => handleDelete(row.id)}
          title="Delete Rule"
        >
          <Delete size={16} />
        </Link>
      </>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: '90px',
  });

  const filteredMasters = masters.filter((master) => {
    const matchesSearch = (
      (master.medicineType || '').toLowerCase().includes(search.toLowerCase()) ||
      (master.dispensingForm || '').toLowerCase().includes(search.toLowerCase()) ||
      (master.doseCount || '').toLowerCase().includes(search.toLowerCase()) ||
      (master.category || '').toLowerCase().includes(search.toLowerCase())
    );
    if (!matchesSearch) return false;
    if (activeCategory === 'TRITURATION' && tritCatFilter !== 'ALL') {
      return master.medicineType === tritCatFilter;
    }
    return true;
  });

  // Helper to get price in matrix
  const getMatrixPrice = (cat, size) => {
    const isPuria = size === 'Puria';
    const numSize = parseFloat(size);
    const rule = masters.find(m => 
      m.category === 'TRITURATION' &&
      m.medicineType === cat &&
      (isPuria ? (m.dispensingForm === 'PURIA' || m.salesUnit === 'PURIA') : parseFloat(m.salesSize) === numSize)
    );
    return rule ? parseFloat(rule.sellingPrice) : null;
  };

  return (
    <>
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
          <div>
            <h3 className="text-primary mb-0">Master of Loose & Dispensing Rules</h3>
            <small className="text-muted">Configure Category wise amounts for Trituration, Mother Tinctures, Dilutions & Packages</small>
          </div>
          <span className="d-flex align-items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Search Rules"
              className="form-control"
              value={search}
              style={{ width: "200px" }}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNotes(!showNotes)}
              className={`btn ${showNotes ? 'btn-info' : 'btn-outline-info'}`}
              title="Operational Notes"
            >
              <Info size={16} /> Notes
            </button>
            <button
              type="button"
              onClick={handleSeed}
              className="btn btn-success"
              title="Seed from Excel Reference"
            >
              <Database size={16} className="me-1" />
              Reset Reference Data
            </button>
            <button type="button" onClick={handleAdd} className="btn btn-primary">
              + Add New Rule
            </button>
          </span>
        </div>

        {/* Operational Notes Panel */}
        {showNotes && (
          <div className="card-body border-bottom bg-light">
            <h6 className="text-info mb-2">📋 Operational Notes & Price Guidelines</h6>
            <div className="row">
              {OPERATIONAL_NOTES.map((note, i) => (
                <div key={i} className="col-md-4 mb-2">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body py-2 px-3">
                      <strong className="text-dark" style={{ fontSize: '0.85rem' }}>{note.title}</strong>
                      <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>{note.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Tabs */}
        <div className="card-body pb-0">
          <ul className="nav nav-pills mb-3 gap-1" style={{ flexWrap: 'wrap' }}>
            {CATEGORIES.map((cat) => (
              <li className="nav-item" key={cat.key}>
                <button
                  className={`nav-link ${activeCategory === cat.key ? 'active' : ''}`}
                  style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    setTritCatFilter('ALL');
                  }}
                >
                  {cat.label}
                  {activeCategory === cat.key && masters.length > 0 && (
                    <span className="badge bg-white text-primary ms-1">{filteredMasters.length}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* TRITURATION MATRIX CARD (When Trituration is active) */}
        {activeCategory === 'TRITURATION' && (
          <div className="card-body pt-0 pb-3">
            <div className="card bg-light border-primary shadow-sm mb-3">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold">
                  📊 Size Conversion Trituration (Apr. 24) - Rate Matrix
                </span>
                <span className="badge bg-white text-primary">All Prices in ₹</span>
              </div>
              <div className="card-body p-0 table-responsive">
                <table className="table table-bordered table-hover text-center mb-0 align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th className="text-start ps-3">Category</th>
                      {TRITURATION_MATRIX_SIZES.map(s => (
                        <th key={s} style={{ minWidth: '90px' }}>{s}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TRITURATION_MATRIX_CATS.map(cat => (
                      <tr key={cat}>
                        <td className="text-start ps-3 fw-bold text-primary">{cat}</td>
                        {TRITURATION_MATRIX_SIZES.map(size => {
                          const price = getMatrixPrice(cat, size);
                          return (
                            <td key={size}>
                              {price != null ? (
                                <span className="badge bg-success bg-opacity-10 text-success fs-13 px-2 py-1">
                                  ₹{price.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sub-filter by Category */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="fw-bold text-muted" style={{ fontSize: '0.85rem' }}>Filter Category:</span>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${tritCatFilter === 'ALL' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setTritCatFilter('ALL')}
                >
                  All
                </button>
                {TRITURATION_MATRIX_CATS.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    className={`btn ${tritCatFilter === cat ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setTritCatFilter(cat)}
                  >
                    {cat.replace('Category ', 'Cat ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="card-body pt-0">
          {loading && <div>Loading...</div>}
          {error && <div className="text-danger">{error}</div>}
          <DataTable
            className="table-hover custom-table"
            columns={getColumns()}
            data={filteredMasters}
            pagination
            striped
            customStyles={customTableStyles}
            progressPending={loading}
            noDataComponent={
              <div className="text-center py-4">
                <p className="mb-2">No rules found for this category.</p>
                <button className="btn btn-sm btn-success" onClick={handleSeed}>
                  <Database size={14} className="me-1" /> Seed from Excel Reference
                </button>
              </div>
            }
          />
        </div>
      </div>
      {showModal && (
        <DispensingMasterForm
          show={showModal}
          onClose={() => setShowModal(false)}
          heading={modalHeading}
          initialData={selectedMaster}
          onSubmit={handleFormSubmit}
          activeCategory={activeCategory}
          materials={materials}
        />
      )}
    </>
  );
};

export default DispensingMaster;
